import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useDispatch, useDispatchItems } from '../hooks/useDispatch';
import { dispatchService } from '../services/dispatchService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';
import { Badge, Modal } from '../components/common';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';

const toDateInputValue = (val: unknown): string => {
  if (!val) return '';
  const s = String(val);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.errors && Array.isArray(error.errors)) return error.errors[0];
  return error?.message || fallback;
};

const formatPickedFrom = (storageSelections: any): string => {
  if (!Array.isArray(storageSelections)) return '';
  return storageSelections
    .map((s: any) => s?.zoneLocation || s?.location || s?.zone || '')
    .filter(Boolean)
    .join(', ');
};

// ── Data model ──────────────────────────────────────────────────────────────
// Every line item lives only in local state until "Submit for Approval" bundles
// the touched ones into one createPastDispatchUpdate request — nothing here
// touches live inventory until an Admin/Super Admin approves it. Storage/pallet
// placement and adding/removing items are intentionally out of scope for this
// workflow (backend does not support re-deriving physical pallet picks here).

interface StagedDispatchItem {
  _key: string;
  _touched: boolean;
  // Snapshot of what this item held on the server before any edit this
  // session — a past-dispatch edit may only reduce a quantity, never raise
  // it past this, so it doubles as the max bound too. Doesn't change as the
  // item is edited, so repeated edits in one session don't drift.
  _originalGoodQty: number;
  _originalCdamQty: number;
  _originalDamQty: number;
  id: number;
  productDescription: string;
  batchNo: string;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  palletCount: number;
  remarks: string;
  storageSelections: any[] | null;
}

const itemFromServer = (item: any): StagedDispatchItem => ({
  _key: String(item.id),
  _touched: false,
  _originalGoodQty: item.goodQty ?? 0,
  _originalCdamQty: item.cdamQty ?? 0,
  _originalDamQty: item.damQty ?? 0,
  id: item.id,
  productDescription: item.productDescription ?? '',
  batchNo: item.batchNo ?? '',
  goodQty: item.goodQty ?? 0,
  cdamQty: item.cdamQty ?? 0,
  damQty: item.damQty ?? 0,
  palletCount: item.palletCount ?? 0,
  remarks: item.remarks ?? '',
  storageSelections: Array.isArray(item.storageSelections) ? item.storageSelections : null,
});

interface ItemFormData {
  batchNo: string;
  goodQty: string;
  cdamQty: string;
  damQty: string;
  palletCount: string;
  remarks: string;
}

const emptyForm: ItemFormData = {
  batchNo: '',
  goodQty: '',
  cdamQty: '0',
  damQty: '0',
  palletCount: '',
  remarks: '',
};

export default function DispatchPastEditPage() {
  const { dispatchId: urlDispatchId } = useParams<{ dispatchId: string }>();
  const dispatchId = urlDispatchId ? parseInt(urlDispatchId, 10) : null;
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  const { data: dispatchData, isLoading: isLoadingDispatch } = useDispatch(dispatchId);
  const { data: dispatchItemsData, isLoading: isLoadingItems } = useDispatchItems(dispatchId);
  const dispatch = (dispatchData as any)?.data ?? dispatchData;

  const [stagedItems, setStagedItems] = useState<StagedDispatchItem[]>([]);
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (!Array.isArray(dispatchItemsData)) return;
    setStagedItems(dispatchItemsData.map(itemFromServer));
    initializedRef.current = true;
  }, [dispatchItemsData]);

  // ── Header fields — staged locally, same as DispatchCreate's fields ─────────
  const [editReason, setEditReason] = useState('');
  const [destination, setDestination] = useState('');
  const [remarks, setRemarks] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [lrNumber, setLrNumber] = useState('');
  const [eWaybillNumber, setEWaybillNumber] = useState('');
  const [lrDate, setLrDate] = useState('');
  const [handlingType, setHandlingType] = useState<'manual' | 'mhe' | ''>('');
  const headerInitRef = useRef(false);
  useEffect(() => {
    if (headerInitRef.current || !dispatch) return;
    setDestination(dispatch.destination || '');
    setRemarks(dispatch.remarks || '');
    setVehicleNo(dispatch.vehicleNo || '');
    setDriverName(dispatch.driverName || '');
    setDriverPhone(dispatch.driverPhone || '');
    setTransporterName(dispatch.transporterName || '');
    setLrNumber(dispatch.lrNumber || '');
    setEWaybillNumber(dispatch.eWaybillNumber || '');
    setLrDate(toDateInputValue(dispatch.lrDate));
    setHandlingType(dispatch.handlingType || '');
    headerInitRef.current = true;
  }, [dispatch]);

  // ── Item form ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<ItemFormData>(emptyForm);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const itemFormRef = useRef<HTMLDivElement>(null);

  const handleEditItem = (item: StagedDispatchItem) => {
    setEditingKey(item._key);
    setFormData({
      batchNo: item.batchNo,
      goodQty: String(item.goodQty),
      cdamQty: String(item.cdamQty),
      damQty: String(item.damQty),
      palletCount: String(item.palletCount),
      remarks: item.remarks,
    });
    itemFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingKey(null);
  };

  const validateForm = (): string | null => {
    if (!formData.batchNo.trim()) return 'Batch number is required';
    const good = parseInt(formData.goodQty);
    const cdam = parseInt(formData.cdamQty);
    const dam = parseInt(formData.damQty);
    const pallets = parseInt(formData.palletCount);
    if (isNaN(good) || good < 0) return 'Good Qty must be 0 or greater';
    if (isNaN(cdam) || cdam < 0) return 'C.DAM Qty must be 0 or greater';
    if (isNaN(dam) || dam < 0) return 'DAM Qty must be 0 or greater';
    if (good + cdam + dam <= 0) return 'Total quantity must be greater than 0';
    if (isNaN(pallets) || pallets < 0) return 'Pallet Count must be 0 or greater';

    // A past-dispatch edit may only correct a quantity downward — raising it
    // above what was originally recorded would fabricate dispatch history
    // that never actually happened, so the cap is the original qty itself,
    // not the original qty plus whatever's since become available in stock.
    const editingItem = stagedItems.find(it => it._key === editingKey);
    if (editingItem) {
      const maxGood = editingItem._originalGoodQty;
      const maxCdam = editingItem._originalCdamQty;
      const maxDam = editingItem._originalDamQty;
      if (good > maxGood) return `Good Qty cannot exceed ${maxGood} — you can only reduce it, not increase past what was originally dispatched`;
      if (cdam > maxCdam) return `C.DAM Qty cannot exceed ${maxCdam} — you can only reduce it, not increase past what was originally dispatched`;
      if (dam > maxDam) return `DAM Qty cannot exceed ${maxDam} — you can only reduce it, not increase past what was originally dispatched`;
    }
    return null;
  };

  const handleUpdateItem = () => {
    if (!editingKey) return;
    const error = validateForm();
    if (error) { toast.error(error); return; }

    setStagedItems(prev => prev.map(it => {
      if (it._key !== editingKey) return it;
      return {
        ...it,
        _touched: true,
        batchNo: formData.batchNo.trim(),
        goodQty: parseInt(formData.goodQty) || 0,
        cdamQty: parseInt(formData.cdamQty) || 0,
        damQty: parseInt(formData.damQty) || 0,
        palletCount: parseInt(formData.palletCount) || 0,
        remarks: formData.remarks.trim(),
      };
    }));
    toast.success('Item updated');
    resetForm();
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!dispatchId) return;
    if (!editReason.trim()) { toast.error('Please provide a reason for this edit'); return; }

    const touchedItems = stagedItems.filter(it => it._touched);
    if (touchedItems.length === 0) { toast.error('Edit at least one item before submitting'); return; }

    if (eWaybillNumber && eWaybillNumber.length !== 12) {
      toast.error('E-Waybill Number must be exactly 12 digits');
      return;
    }
    const vehicleNoError = FIELD_VALIDATORS.vehicleNumber(vehicleNo, false);
    if (vehicleNoError) { toast.error(vehicleNoError); return; }
    const driverPhoneError = FIELD_VALIDATORS.mobileNumber(driverPhone, false);
    if (driverPhoneError) { toast.error(driverPhoneError); return; }
    const lrNumberError = FIELD_VALIDATORS.lrNumber(lrNumber);
    if (lrNumberError) { toast.error(lrNumberError); return; }

    const ok = await confirm({
      title: 'Submit for approval?',
      message: 'This request will be sent to an Admin/Super Admin for review. Nothing changes until it is approved.',
      confirmText: 'Submit',
    });
    if (!ok) return;

    setIsSubmitting(true);
    try {
      await dispatchService.createPastDispatchUpdate(dispatchId, {
        editReason: editReason.trim(),
        header: {
          destination: destination.trim() || undefined,
          lrNumber: lrNumber.trim() || undefined,
          eWaybillNumber: eWaybillNumber.trim() || undefined,
          vehicleNo: vehicleNo.trim() || undefined,
          driverName: driverName.trim() || undefined,
          driverPhone: driverPhone.trim() || undefined,
          transporterName: transporterName.trim() || undefined,
          lrDate: lrDate || undefined,
          handlingType: handlingType || undefined,
          remarks: remarks.trim() || undefined,
        } as any,
        items: touchedItems.map(it => ({
          dispatchItemId: it.id,
          goodQty: it.goodQty,
          cdamQty: it.cdamQty,
          damQty: it.damQty,
          palletCount: it.palletCount,
          batchNo: it.batchNo || undefined,
          remarks: it.remarks || undefined,
        })),
      });

      toast.success('Edit request submitted for admin approval');
      navigate('/outward');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to submit edit request'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingDispatch || isLoadingItems) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading dispatch…</p>
          </div>
        </div>
      </Layout>
    );
  }

  const editingItem = editingKey ? stagedItems.find(it => it._key === editingKey) : null;
  const maxGood = editingItem ? editingItem._originalGoodQty : null;
  const maxCdam = editingItem ? editingItem._originalCdamQty : null;
  const maxDam = editingItem ? editingItem._originalDamQty : null;

  // Shared column definitions drive both the mobile card list and desktop table.
  const itemColumns: TableColumn[] = [
    { key: 'productDescription', label: 'Product' },
    { key: 'batchNo', label: 'Batch No', render: v => v || '-' },
    { key: 'goodQty', label: 'Good Out', align: 'right' },
    { key: 'cdamQty', label: 'CDAM Out', align: 'right' },
    { key: 'damQty', label: 'DAM Out', align: 'right' },
    { key: 'palletCount', label: 'Pallets Out', align: 'right', render: v => v || 0 },
    { key: 'pickedFrom', label: 'Picked From', render: (_v, row) => formatPickedFrom(row.storageSelections) || '-' },
    {
      key: 'status',
      label: 'Status',
      render: (_v, row) => row._touched
        ? <Badge variant="warning" size="sm" rounded>Edited</Badge>
        : <span className="text-gray-400">—</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_v, row) => (
        <button onClick={() => handleEditItem(row)} className="text-brand-600 hover:text-brand-800 text-xs">
          ✏️ Edit
        </button>
      ),
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-1 py-6 lg:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="mb-6 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-100 p-6 rounded-2xl shadow-xl border-2 border-brand-300 hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Edit Past Dispatch</h2>
                <p className="text-gray-700 mt-1 font-medium">
                  Outbound Processing{dispatch?.dispatchNumber ? ` — ${dispatch.dispatchNumber}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/outward')}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 border-2 border-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="whitespace-nowrap">Back to Outbound</span>
            </button>
          </div>
        </div>

        {/* Status Bar */}
        <div className="mb-4 p-4 rounded-xl flex flex-wrap justify-between items-center gap-2 shadow-lg transform transition-all duration-300 hover:scale-[1.01] bg-yellow-50 border border-yellow-200">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            <span className="font-semibold text-yellow-700">Editing an approved dispatch — changes require Admin/Super Admin re-approval</span>
          </div>
          {dispatch?.dispatchNumber && (
            <span className="text-sm text-gray-600">
              Dispatch: <span className="font-semibold">{dispatch.dispatchNumber}</span>
            </span>
          )}
        </div>

        {/* Dispatch Header - Read Only */}
        <div className="card mb-6 transform transition-all duration-300 hover:shadow-xl animate-fade-in">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg mr-3 shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Dispatch Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-gradient-to-r from-brand-50 to-brand-50 p-5 rounded-xl border-2 border-brand-200 shadow-sm">
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Dispatch Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatch?.dispatchNumber || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Client Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatch?.clientName || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Destination</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatch?.destination || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Vehicle Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatch?.vehicleNo || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Driver Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatch?.driverName || '-'}</p>
            </div>
          </div>
        </div>

        {/* Reason for edit */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-comment-dots text-rose-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Reason for Edit <span className="text-red-500">*</span></h3>
              <p className="text-xs text-gray-500 mt-0.5">Explain why this dispatch needs to change</p>
            </div>
          </div>
          <textarea
            value={editReason}
            onChange={e => setEditReason(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm transition-colors"
            placeholder="Why does this dispatch need to change?"
          />
        </div>

        {/* Dispatch Information */}
        <div className="card mb-6 overflow-hidden">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-truck text-brand-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Dispatch Information</h3>
              <p className="text-xs text-gray-500 mt-0.5">Optional details attached to this dispatch</p>
            </div>
          </div>
          <div className="space-y-5">
            {/* Transport fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-car text-orange-400 text-xs"></i>
                  Vehicle Number
                </label>
                <input value={vehicleNo} onChange={e => setVehicleNo(INPUT_SANITIZERS.vehicleFormat(e.target.value))}
                  placeholder="e.g. MH04AB1234"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-user text-orange-400 text-xs"></i>
                  Driver Name
                </label>
                <input value={driverName} onChange={e => setDriverName(e.target.value)}
                  placeholder="e.g. Raju Patil"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-phone text-orange-400 text-xs"></i>
                  Driver Phone
                </label>
                <input value={driverPhone} onChange={e => setDriverPhone(INPUT_SANITIZERS.digitsOnly(e.target.value).slice(0, 10))}
                  placeholder="e.g. 9876543210"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-building text-orange-400 text-xs"></i>
                  Transporter Name
                </label>
                <input value={transporterName} onChange={e => setTransporterName(INPUT_SANITIZERS.companyNameFormat(e.target.value))}
                  placeholder="e.g. Fast Cargo Pvt Ltd"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-calendar text-orange-400 text-xs"></i>
                  LR Date
                </label>
                <input type="date" value={lrDate} onChange={e => setLrDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-dolly text-orange-400 text-xs"></i>
                  Handling Type
                </label>
                <select value={handlingType} onChange={e => setHandlingType(e.target.value as any)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors">
                  <option value="">— Select —</option>
                  <option value="manual">Manual</option>
                  <option value="mhe">MHE</option>
                </select>
              </div>
            </div>

            {/* Lorry-receipt fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-receipt text-brand-400 text-xs"></i>
                  LR Number <span className="text-gray-400 font-normal text-xs">(Lorry Receipt)</span>
                </label>
                <input value={lrNumber} onChange={e => setLrNumber(INPUT_SANITIZERS.lrNumberFormat(e.target.value))}
                  placeholder="e.g. LR2026001234"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-file-invoice text-green-400 text-xs"></i>
                  E-Waybill Number
                </label>
                <input value={eWaybillNumber} onChange={e => setEWaybillNumber(INPUT_SANITIZERS.digitsOnly(e.target.value).slice(0, 12))}
                  placeholder="12-digit E-Waybill Number"
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors ${
                    eWaybillNumber && eWaybillNumber.length !== 12 ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`} />
                <p className={`text-xs ${eWaybillNumber && eWaybillNumber.length !== 12 ? 'text-red-500' : 'text-gray-400'}`}>
                  {eWaybillNumber
                    ? eWaybillNumber.length === 12 ? '✓ Valid (12 digits)' : `${eWaybillNumber.length}/12 digits`
                    : 'Must be exactly 12 digits'}
                </p>
              </div>
            </div>

            {/* Address / notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-map-marker-alt text-red-400 text-xs"></i>
                  Destination
                </label>
                <textarea value={destination} onChange={e => setDestination(e.target.value)} rows={4} maxLength={200}
                  placeholder="Street Address&#10;City, State, ZIP Code"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors" />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-400">Complete delivery address</p>
                  <span className={`text-xs font-medium tabular-nums ${destination.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                    {destination.length}/200
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-sticky-note text-amber-400 text-xs"></i>
                  Additional Information
                </label>
                <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={4} maxLength={200}
                  placeholder="Remarks, special instructions, or notes for this dispatch..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors" />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-400">Included with dispatch records</p>
                  <span className={`text-xs font-medium tabular-nums ${remarks.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                    {remarks.length}/200
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="card mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Dispatch Items ({stagedItems.length})
          </h3>

          {stagedItems.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-600">No items on this dispatch.</p>
            </div>
          ) : (
            <>
              <MobileCardList columns={itemColumns} data={stagedItems} />
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch No</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Good Out</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CDAM Out</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">DAM Out</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pallets Out</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Picked From</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stagedItems.map(item => (
                      <tr key={item._key} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.productDescription}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.batchNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.goodQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.cdamQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.damQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.palletCount || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatPickedFrom(item.storageSelections) || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          {item._touched
                            ? <Badge variant="warning" size="sm" rounded>Edited</Badge>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button onClick={() => handleEditItem(item)} className="text-brand-600 hover:text-brand-800 text-xs" title="Edit item">
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end pb-8">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-lg font-semibold"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Submit for Approval
              </>
            )}
          </button>
        </div>

        {/* Edit Item Modal */}
        <Modal
          isOpen={!!editingKey}
          onClose={resetForm}
          title="Edit Item - Update Quantities"
          size="lg"
          footer={
            <div className="flex justify-end gap-3">
              <button onClick={resetForm} className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
                Cancel
              </button>
              <button onClick={handleUpdateItem} className="px-6 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">
                Update Item
              </button>
            </div>
          }
        >
          {editingItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Product</p>
                <p className="font-semibold text-gray-900">{editingItem.productDescription}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Batch No <span className="text-red-500">*</span></label>
                  <input value={formData.batchNo} onChange={e => setFormData(f => ({ ...f, batchNo: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Good Qty <span className="text-red-500">*</span>
                    {maxGood != null && <span className="text-gray-400 font-normal text-xs"> (max {maxGood})</span>}
                  </label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.goodQty} onChange={e => setFormData(f => ({ ...f, goodQty: INPUT_SANITIZERS.digitsOnly(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    C.DAM Qty
                    {maxCdam != null && <span className="text-gray-400 font-normal text-xs"> (max {maxCdam})</span>}
                  </label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.cdamQty} onChange={e => setFormData(f => ({ ...f, cdamQty: INPUT_SANITIZERS.digitsOnly(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    DAM Qty
                    {maxDam != null && <span className="text-gray-400 font-normal text-xs"> (max {maxDam})</span>}
                  </label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.damQty} onChange={e => setFormData(f => ({ ...f, damQty: INPUT_SANITIZERS.digitsOnly(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Pallet Count</label>
                  <input type="number" value={formData.palletCount} readOnly disabled
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" />
                  <p className="text-xs text-gray-400">Pallet count can't be changed here</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">Remarks</label>
                <textarea value={formData.remarks} onChange={e => setFormData(f => ({ ...f, remarks: e.target.value }))} rows={3} maxLength={200}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm transition-colors" />
              </div>
            </div>
          )}
        </Modal>
      </div>
      <ConfirmDialog />
    </Layout>
  );
}
